import { z } from "zod";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const L_DELAY = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.delay-drawer`;

export const DELAY_FORM_FIELDS = {
  HOLD_NO: "holdNo",
  DELAY_TYPE_CODE: "delayTypeCode",
  DELAY_CODE: "delayCode",
  START_AT: "startAt",
  END_AT: "endAt",
  DELAY_TIME: "delayTime",
  LOADER_CODE: "loaderCode",
  REMARKS: "remarks",
} as const;

export const REMARKS_MAX_LENGTH = 1000;

/** The operation's window for a delay (AD-96): dates are Asia/Dubai wall-clock `Date`s, `now` is the same clock. */
export interface DelayWindow {
  status: string | null | undefined;
  firstAts: Date | null;
  lastAtc: Date | null;
  now: () => Date;
}

export const delaySchema = (t: Translate, window: DelayWindow) =>
  z
    .object({
      [DELAY_FORM_FIELDS.HOLD_NO]: z
        .string()
        .min(1, t("form-validation.mandatory", { label: t(`${L_DELAY}.fields.hold-no`) })),
      [DELAY_FORM_FIELDS.DELAY_TYPE_CODE]: z
        .string()
        .min(1, t("form-validation.mandatory", { label: t(`${L_DELAY}.fields.delay-type`) })),
      [DELAY_FORM_FIELDS.DELAY_CODE]: z
        .string()
        .min(1, t("form-validation.mandatory", { label: t(`${L_DELAY}.fields.delay-code`) })),
      [DELAY_FORM_FIELDS.START_AT]: z.date({
        required_error: t("form-validation.mandatory", { label: t(`${L_DELAY}.fields.start`) }),
        invalid_type_error: t("form-validation.mandatory", {
          label: t(`${L_DELAY}.fields.start`),
        }),
      }),
      [DELAY_FORM_FIELDS.END_AT]: z.date({
        required_error: t("form-validation.mandatory", { label: t(`${L_DELAY}.fields.end`) }),
        invalid_type_error: t("form-validation.mandatory", { label: t(`${L_DELAY}.fields.end`) }),
      }),
      [DELAY_FORM_FIELDS.DELAY_TIME]: z.string().optional(),
      [DELAY_FORM_FIELDS.LOADER_CODE]: z.string().nullable().optional(),
      [DELAY_FORM_FIELDS.REMARKS]: z
        .string()
        .max(REMARKS_MAX_LENGTH, t(`${L_DELAY}.errors.remarks-too-long`))
        .optional(),
    })
    .superRefine((values, context) => {
      const { startAt, endAt } = values;
      if (!(startAt instanceof Date) || !(endAt instanceof Date)) {
        return;
      }
      if (endAt.getTime() <= startAt.getTime()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [DELAY_FORM_FIELDS.END_AT],
          message: t(`${L_DELAY}.errors.end-before-start`),
        });
      }
      if (window.firstAts && startAt.getTime() < window.firstAts.getTime()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [DELAY_FORM_FIELDS.START_AT],
          message: t(`${L_DELAY}.errors.start-before-first-ats`),
        });
      }
      if (window.status === "ONGOING" && endAt.getTime() > window.now().getTime()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [DELAY_FORM_FIELDS.END_AT],
          message: t(`${L_DELAY}.errors.end-in-future`),
        });
      }
      if (
        window.status === "WAITING_FINAL_DRAFT_SURVEY" &&
        window.lastAtc &&
        endAt.getTime() > window.lastAtc.getTime()
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [DELAY_FORM_FIELDS.END_AT],
          message: t(`${L_DELAY}.errors.end-after-last-atc`),
        });
      }
    });

export type DelayFormData = z.infer<ReturnType<typeof delaySchema>>;

export const delayDefaultValues = {
  holdNo: "",
  delayTypeCode: "",
  delayCode: "",
  startAt: undefined,
  endAt: undefined,
  delayTime: "",
  loaderCode: "",
  remarks: "",
} as unknown as DelayFormData;
